// src/components/ClienteDetalhesModal.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import './ClienteDetalhesModal.css';
import { 
    FaTimes, FaCalendarAlt, FaDollarSign, FaChartLine, FaHistory, 
    FaCommentDots, FaEnvelope, FaSms, FaSpinner, FaCheckCircle, 
    FaExclamationCircle, FaMapMarkerAlt, FaPhone, FaIdCard, FaListUl, 
    FaEye, FaExpand, FaPaperclip, FaSave, FaBullhorn
} from 'react-icons/fa';

export default function ClienteDetalhesModal({ clienteId, onClose }) {
    const [details, setDetails] = useState(null);
    const [history, setHistory] = useState([]);
    const [comunicacoes, setComunicacoes] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estado para os Modais Secundários
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false); // Modal Editor de E-mail
    const [activeTabLeft, setActiveTabLeft] = useState('servicos');

    // Estados do Formulário de Comunicação
    const [tipoComunicacao, setTipoComunicacao] = useState('SMS');
    const [assunto, setAssunto] = useState(''); 
    const [mensagem, setMensagem] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [feedback, setFeedback] = useState({ tipo: '', texto: '' }); 
    
    // Estado para Anexos
    const [anexo, setAnexo] = useState(null);
    const fileInputRef = useRef(null);

    const fetchDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/clients/${clienteId}/details`);
            setDetails(response.data.details);
            setHistory(response.data.history);
            setComunicacoes(response.data.comunicacoes || []); 
        } catch (err) {
            setError('Não foi possível carregar o dossiê do cliente.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [clienteId]);

    useEffect(() => {
        if (clienteId) fetchDetails();
    }, [clienteId, fetchDetails]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setAnexo(e.target.files[0]);
        }
    };

    const handleComunicacaoSubmit = async (e) => {
        e.preventDefault();
        
        if (!mensagem.trim()) {
            setFeedback({ tipo: 'erro', texto: 'A mensagem não pode estar vazia.' });
            return;
        }

        if (tipoComunicacao === 'Email') {
            if (!details?.email || !details.email.includes('@')) {
                setFeedback({ tipo: 'erro', texto: 'O cliente não possui um e-mail válido cadastrado.' });
                return;
            }
            if (!assunto.trim()) {
                setFeedback({ tipo: 'erro', texto: 'O campo assunto é obrigatório para e-mails.' });
                return;
            }
        } else if (tipoComunicacao === 'SMS') {
            if (!details?.telefone) {
                setFeedback({ tipo: 'erro', texto: 'O cliente não possui telefone cadastrado.' });
                return;
            }
        }

        setIsSending(true);
        setFeedback({ tipo: '', texto: '' }); 

        try {
            let dataToSend;

            // Se for E-mail E tiver anexo, usamos o FormData (Multipart) para enviar o arquivo físico
            if (tipoComunicacao === 'Email' && anexo) {
                dataToSend = new FormData();
                dataToSend.append('tipo', tipoComunicacao);
                dataToSend.append('mensagem', mensagem);
                dataToSend.append('emailDestino', details.email);
                dataToSend.append('assunto', assunto);
                dataToSend.append('anexo', anexo); // Adiciona o arquivo cru
            } else {
                // Se for SMS ou E-mail sem anexo, mandamos um JSON simples e mais rápido
                dataToSend = {
                    tipo: tipoComunicacao,
                    mensagem: mensagem,
                    emailDestino: tipoComunicacao === 'Email' ? details.email : undefined,
                    assunto: tipoComunicacao === 'Email' ? assunto : undefined
                };
            }

            await api.post(`/api/clients/${clienteId}/comunicacao`, dataToSend);
            
            setFeedback({ tipo: 'sucesso', texto: `${tipoComunicacao} enviado com sucesso!` });
            
            // Limpa o formulário e o anexo
            setMensagem(''); 
            setAnexo(null);
            if (tipoComunicacao === 'Email') setAssunto('');
            
            // Atualiza os dados e abre o histórico para mostrar o envio
            fetchDetails(); 
            setActiveTabLeft('mensagens');
            setIsHistoricoOpen(true);
            setIsEditorOpen(false); // Fecha o editor se ele estiver aberto

        } catch (error) {
            const errorMsg = error.response?.data?.error || `Falha de comunicação com o provedor.`;
            setFeedback({ tipo: 'erro', texto: errorMsg });
        } finally {
            setIsSending(false);
            setTimeout(() => setFeedback({ tipo: '', texto: '' }), 6000);
        }
    };

    // --- COMPONENTE DO MODAL DE HISTÓRICO ---
    const ModalHistorico = () => {
        if (!isHistoricoOpen) return null;

        return (
            <div className="cdm-historico-overlay" onClick={() => setIsHistoricoOpen(false)}>
                <div className="cdm-historico-modal" onClick={e => e.stopPropagation()}>
                    <div className="cdm-hm-header">
                        <h2>Histórico do Cliente</h2>
                        <button onClick={() => setIsHistoricoOpen(false)} className="cdm-btn-close"><FaTimes /></button>
                    </div>
                    
                    <div className="cdm-history-tabs">
                        <button 
                            className={`cdm-h-tab ${activeTabLeft === 'servicos' ? 'active' : ''}`}
                            onClick={() => setActiveTabLeft('servicos')}
                        >
                            <FaListUl /> Ordens de Serviço
                        </button>
                        <button 
                            className={`cdm-h-tab ${activeTabLeft === 'mensagens' ? 'active' : ''}`}
                            onClick={() => setActiveTabLeft('mensagens')}
                        >
                            <FaCommentDots /> Mensagens Enviadas
                        </button>
                    </div>

                    <div className="cdm-hm-body">
                        {activeTabLeft === 'servicos' && (
                            <div className="cdm-timeline animate-fade-in">
                                {history.length > 0 ? history.map(servico => (
                                    <div key={servico.id} className="cdm-tl-item">
                                        <div className="cdm-tl-dot"></div>
                                        <div className="cdm-tl-card">
                                            <div className="cdm-tl-header">
                                                <span className="cdm-tl-id">OS #{servico.id}</span>
                                                <span className="cdm-tl-date">{format(new Date(servico.data_criacao), 'dd MMM yy, HH:mm', { locale: ptBR })}</span>
                                            </div>
                                            <p className="cdm-tl-desc">{servico.descricao}</p>
                                            <div className="cdm-tl-footer">
                                                <span className={`cdm-os-status status-${servico.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                                                    {servico.status}
                                                </span>
                                                <span className="cdm-tl-value">{parseFloat(servico.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="cdm-empty-timeline">Nenhuma Ordem de Serviço encontrada.</div>
                                )}
                            </div>
                        )}

                        {activeTabLeft === 'mensagens' && (
                            <div className="cdm-timeline animate-fade-in">
                                {comunicacoes.length > 0 ? comunicacoes.map(com => {
                                    // LOGICA PARA IDENTIFICAR COMUNICAÇÃO EM MASSA
                                    const isMassa = com.assunto && (com.assunto.includes('Comunicação em Massa') || com.assunto.includes('[Em Massa]'));
                                    
                                    // Limpa o assunto para exibição caso seja e-mail em massa
                                    const displayAssunto = com.assunto ? com.assunto.replace('[Em Massa] ', '') : null;

                                    return (
                                        <div key={com.id} className="cdm-tl-item">
                                            <div className={`cdm-tl-dot ${com.tipo.toLowerCase()}`}></div>
                                            <div className={`cdm-tl-card ${isMassa ? 'massa-card' : ''}`}>
                                                <div className="cdm-tl-header">
                                                    <div className="cdm-tl-tags-wrapper">
                                                        <span className="cdm-tl-id">
                                                            {com.tipo.toUpperCase() === 'SMS' ? <span className="badge-com sms"><FaSms /> SMS</span> : <span className="badge-com email"><FaEnvelope /> E-mail</span>}
                                                        </span>
                                                        {isMassa && <span className="badge-massa"><FaBullhorn /> Comunicação em Massa</span>}
                                                    </div>
                                                    <span className="cdm-tl-date">{format(new Date(com.data_criacao), 'dd MMM yy, HH:mm', { locale: ptBR })}</span>
                                                </div>
                                                
                                                {displayAssunto && displayAssunto !== 'Comunicação em Massa' && (
                                                    <strong className="cdm-tl-subject">Assunto: {displayAssunto}</strong>
                                                )}
                                                
                                                <div className="cdm-tl-desc" dangerouslySetInnerHTML={{ __html: com.mensagem }}></div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="cdm-empty-timeline">Nenhuma comunicação registrada no histórico.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // --- COMPONENTE DO MODAL EDITOR EXPANDIDO ---
    const ModalEditorFullscreen = () => {
        if (!isEditorOpen) return null;

        return (
            <div className="cdm-historico-overlay" onClick={() => setIsEditorOpen(false)}>
                <div className="cdm-editor-fullscreen" onClick={e => e.stopPropagation()}>
                    <div className="cdm-ef-header">
                        <div className="cdm-ef-titles">
                            <h2><FaEnvelope /> Editor de E-mail Expandido</h2>
                            <span>Para: {details?.email}</span>
                        </div>
                        <button onClick={() => setIsEditorOpen(false)} className="cdm-btn-close"><FaTimes /></button>
                    </div>
                    
                    <div className="cdm-ef-body">
                        <div className="cdm-input-group">
                            <label>Assunto</label>
                            <input 
                                type="text" 
                                value={assunto}
                                onChange={(e) => setAssunto(e.target.value)}
                                placeholder="Digite o assunto..."
                                className="cdm-ef-input"
                            />
                        </div>
                        <div className="cdm-input-group flex-grow">
                            <label>Corpo do E-mail</label>
                            <textarea
                                value={mensagem}
                                onChange={(e) => setMensagem(e.target.value)}
                                placeholder="Escreva sua mensagem aqui. Você pode usar tags HTML básicas como <b>, <br>, <i>..."
                                className="cdm-ef-textarea"
                            ></textarea>
                        </div>
                    </div>

                    <div className="cdm-ef-footer">
                        <button onClick={() => setIsEditorOpen(false)} className="cdm-btn-outline">
                            <FaSave /> Salvar e Voltar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading && !details) return (
            <div className="cdm-state-box">
                <FaSpinner className="cdm-icon-spin" />
                <p>Construindo dossiê do cliente...</p>
            </div>
        );
        if (error) return (
            <div className="cdm-state-box error">
                <FaExclamationCircle className="cdm-icon-error" />
                <p>{error}</p>
            </div>
        );
        if (!details) return null;

        const { 
            nome, email, telefone, endereco, cpf_cnpj,
            faturamento_total, lucro_total, percentual_lucro, 
            total_servicos, primeiro_servico_data, dias_desde_ultimo_servico 
        } = details;

        return (
            <>
                <header className="cdm-header">
                    <div className="cdm-header-profile">
                        <div className="cdm-avatar">{nome.charAt(0).toUpperCase()}</div>
                        <div className="cdm-header-titles">
                            <h2>{nome}</h2>
                            <div className="cdm-header-badges">
                                <span className="cdm-badge-status">
                                    Cliente desde {primeiro_servico_data ? format(new Date(primeiro_servico_data), 'MMM yyyy', { locale: ptBR }) : 'Novo'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="cdm-btn-close" title="Fechar (Esc)"><FaTimes /></button>
                </header>

                <div className="cdm-grid-layout">
                    {/* COLUNA ESQUERDA: INFORMAÇÕES LIMPAS */}
                    <div className="cdm-col-main">
                        
                        <div className="cdm-quick-info">
                            <div className="cdm-info-pill" title="Documento"><FaIdCard /> {cpf_cnpj || 'Não informado'}</div>
                            <div className="cdm-info-pill" title="Telefone"><FaPhone /> {telefone || 'Não informado'}</div>
                            <div className="cdm-info-pill" title="E-mail"><FaEnvelope /> {email || 'Não informado'}</div>
                            <div className="cdm-info-pill" title="Endereço"><FaMapMarkerAlt /> {endereco || 'Não informado'}</div>
                        </div>

                        <div className="cdm-metrics-grid">
                            <div className="cdm-metric-card">
                                <div className="cdm-mc-icon blue"><FaDollarSign /></div>
                                <div className="cdm-mc-data">
                                    <label>Faturamento Bruto</label>
                                    <strong>{parseFloat(faturamento_total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                                </div>
                            </div>
                            <div className="cdm-metric-card">
                                <div className={`cdm-mc-icon ${lucro_total >= 0 ? 'green' : 'red'}`}><FaChartLine /></div>
                                <div className="cdm-mc-data">
                                    <label>Lucro Estimado ({percentual_lucro?.toFixed(1) || 0}%)</label>
                                    <strong className={lucro_total >= 0 ? 'text-green' : 'text-red'}>
                                        {parseFloat(lucro_total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </strong>
                                </div>
                            </div>
                            <div className="cdm-metric-card">
                                <div className="cdm-mc-icon purple"><FaHistory /></div>
                                <div className="cdm-mc-data">
                                    <label>Volume de Serviços</label>
                                    <strong>{total_servicos} OS finalizadas</strong>
                                </div>
                            </div>
                            <div className="cdm-metric-card">
                                <div className={`cdm-mc-icon ${dias_desde_ultimo_servico > 90 ? 'orange' : 'gray'}`}><FaCalendarAlt /></div>
                                <div className="cdm-mc-data">
                                    <label>Engajamento</label>
                                    <strong className={dias_desde_ultimo_servico > 90 ? 'text-orange' : ''}>
                                        {dias_desde_ultimo_servico === null ? 'Sem histórico' : `Inativo há ${dias_desde_ultimo_servico} dias`}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        <div className="cdm-history-trigger">
                            <button onClick={() => setIsHistoricoOpen(true)} className="cdm-btn-history">
                                <FaEye /> Visualizar Histórico Completo de Serviços e Mensagens
                            </button>
                        </div>
                    </div>

                    {/* COLUNA DIREITA: CENTRAL DE COMUNICAÇÃO */}
                    <aside className="cdm-col-sidebar">
                        <div className="cdm-com-panel">
                            <h3 className="cdm-section-title"><FaCommentDots /> Nova Mensagem</h3>
                            <p className="cdm-com-subtitle">Dispare mensagens diretas integradas aos seus provedores.</p>
                            
                            <form onSubmit={handleComunicacaoSubmit} className="cdm-form">
                                <div className="cdm-tabs">
                                    <button 
                                        type="button" 
                                        className={`cdm-tab ${tipoComunicacao === 'SMS' ? 'active sms' : ''}`} 
                                        onClick={() => setTipoComunicacao('SMS')}
                                    >
                                        <FaSms/> Comtele SMS
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`cdm-tab ${tipoComunicacao === 'Email' ? 'active email' : ''}`} 
                                        onClick={() => setTipoComunicacao('Email')}
                                    >
                                        <FaEnvelope/> Brevo E-mail
                                    </button>
                                </div>

                                <div className="cdm-form-fields">
                                    {tipoComunicacao === 'Email' && (
                                        <div className="cdm-input-group animate-slide-down">
                                            <label>Assunto do E-mail</label>
                                            <input 
                                                type="text" 
                                                value={assunto}
                                                onChange={(e) => setAssunto(e.target.value)}
                                                placeholder="Ex: Nota Fiscal do seu Serviço"
                                                disabled={isSending}
                                                required={tipoComunicacao === 'Email'}
                                            />
                                        </div>
                                    )}

                                    <div className="cdm-input-group relative-group">
                                        <div className="cdm-label-row">
                                            <label>Mensagem</label>
                                            {tipoComunicacao === 'Email' && (
                                                <button type="button" onClick={() => setIsEditorOpen(true)} className="cdm-btn-expand" title="Abrir Editor Tela Cheia">
                                                    <FaExpand /> Expandir Editor
                                                </button>
                                            )}
                                        </div>
                                        <textarea
                                            value={mensagem}
                                            onChange={(e) => setMensagem(e.target.value)}
                                            placeholder={`Escreva sua mensagem de ${tipoComunicacao} aqui...`}
                                            disabled={isSending}
                                            className={tipoComunicacao === 'Email' ? 'textarea-email' : ''}
                                        ></textarea>
                                        
                                        <div className="cdm-form-tools-row">
                                            <div className="cdm-char-counter">
                                                {tipoComunicacao === 'SMS' 
                                                    ? `${mensagem.length}/160 caracteres (Aprox. ${Math.ceil((mensagem.length || 1)/160)} SMS)`
                                                    : 'Suporta formatação HTML básica'
                                                }
                                            </div>
                                            
                                            {tipoComunicacao === 'Email' && (
                                                <div className="cdm-attachment-tool">
                                                    <input 
                                                        type="file" 
                                                        ref={fileInputRef} 
                                                        onChange={handleFileChange}
                                                        style={{ display: 'none' }}
                                                    />
                                                    
                                                    {!anexo ? (
                                                        <button type="button" onClick={() => fileInputRef.current.click()} className="cdm-btn-attach">
                                                            <FaPaperclip /> Anexar Arquivo
                                                        </button>
                                                    ) : (
                                                        <div className="cdm-attached-chip">
                                                            <FaPaperclip className="cdm-chip-icon" />
                                                            <span className="cdm-chip-text" title={anexo.name}>{anexo.name}</span>
                                                            <button type="button" className="cdm-chip-remove" onClick={() => setAnexo(null)} title="Remover Anexo">
                                                                <FaTimes />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {feedback.texto && (
                                    <div className={`cdm-feedback ${feedback.tipo}`}>
                                        {feedback.tipo === 'sucesso' ? <FaCheckCircle /> : <FaExclamationCircle />}
                                        <span>{feedback.texto}</span>
                                    </div>
                                )}

                                <button type="submit" className={`cdm-btn-submit ${tipoComunicacao.toLowerCase()}`} disabled={isSending}>
                                    {isSending ? (
                                        <><FaSpinner className="cdm-icon-spin"/> Disparando {tipoComunicacao}...</>
                                    ) : (
                                        <>Enviar {tipoComunicacao}</>
                                    )}
                                </button>
                            </form>
                        </div>
                    </aside>
                </div>
            </>
        );
    };

    return (
        <div className="cdm-overlay" onClick={onClose}>
            <div className="cdm-modal" onClick={e => e.stopPropagation()}>
                {renderContent()}
            </div>
            {/* Modais Secundários */}
            <ModalHistorico />
            <ModalEditorFullscreen />
        </div>
    );
}